import type { AxiosInstance } from "axios";
import type { output, ZodType } from "zod";

import { parseAxiosResponse } from "@thalamos/common";
import { attachmentFilenameOf } from "@thalamos/frontend";
import type {
  Assessment,
  AssessmentHistoryPage,
  AssessmentId,
  DecideAssessmentRequest,
  DeleteAssessmentRequest,
  Person,
  PersonAssessments,
  PersonId,
  StartAssessmentRequest,
  SubmissionNumber,
  SubmitAssessmentRequest,
} from "@thalamos/haven-common";
import {
  AssessmentHistoryPageSchema,
  AssessmentSchema,
  CarryOverOfferSchema,
  DecisionRecordedSchema,
  notificationChannels,
  PersonAssessmentsResponseSchema,
  personChannels,
  SubmissionRecordedSchema,
} from "@thalamos/haven-common";

import type { ErrorBody, NoContentBody, ResponseBody } from "../axios/api.js";
import { useLiveApiAxios } from "../axios/api.js";

const parseAssessments = parseAxiosResponse(PersonAssessmentsResponseSchema);
const parseAssessment = parseAxiosResponse(AssessmentSchema);
const parseHistory = parseAxiosResponse(AssessmentHistoryPageSchema);

/**
 * A created resource answers 201, which `parseAxiosResponse` leaves alone, so
 * its body is parsed here; any other status keeps its error body.
 */
const parseCreated =
  <S extends ZodType>(schema: S) =>
  (response: {
    status: number;
    data: unknown;
  }): ResponseBody<output<S>, ErrorBody> =>
    response.status === 201
      ? schema.parse(response.data)
      : (response.data as ErrorBody);

const parseStarted = parseCreated(AssessmentSchema);
const parseSubmitted = parseCreated(SubmissionRecordedSchema);
const parseDecided = parseCreated(DecisionRecordedSchema);

export const config = {
  assessment: {
    forPerson: (personId: string) => ({
      url: `/api/persons/${personId}/assessments`,
      method: "get",
      transformResponse: parseAssessments,
    }),
    historyForPerson: (personId: string, paging: { limit: number }) => ({
      url: `/api/persons/${personId}/assessments/history`,
      method: "get",
      params: paging,
      transformResponse: parseHistory,
    }),
    byId: (assessmentId: string) => ({
      url: `/api/assessments/${assessmentId}`,
      method: "get",
      transformResponse: parseAssessment,
    }),
    pdf: (assessmentId: AssessmentId) => ({
      url: `/api/assessments/${assessmentId}/pdf`,
      method: "get",
      responseType: "blob" as const,
    }),
    start: (personId: PersonId, body: StartAssessmentRequest) => ({
      url: `/api/persons/${personId}/assessments`,
      method: "post",
      data: body,
    }),
    carryOverOffer: (personId: PersonId) => ({
      url: `/api/persons/${personId}/assessments/carry-over-offer`,
      method: "get",
      // The start dialog waits on this, so an answer that never comes must not
      // keep anyone from starting: past this, the start goes on without it.
      timeout: 10_000,
    }),
    submit: (assessmentId: AssessmentId, body: SubmitAssessmentRequest) => ({
      url: `/api/assessments/${assessmentId}/submissions`,
      method: "post",
      data: body,
    }),
    /** Takes an assessment out of use; answers 204, and is undone by undeleting it. */
    delete: (assessmentId: AssessmentId, body: DeleteAssessmentRequest) => ({
      url: `/api/assessments/${assessmentId}/delete`,
      method: "post",
      data: body,
    }),
    /**
     * Approves or returns one submission, named by its number so a decision
     * made from a stale page is refused rather than landing on a later one.
     */
    decide: (
      assessmentId: AssessmentId,
      submissionNumber: SubmissionNumber,
      body: DecideAssessmentRequest,
    ) => ({
      url: `/api/assessments/${assessmentId}/submissions/${String(submissionNumber)}/decision`,
      method: "post",
      data: body,
    }),
  },
};

export const api = (apiAxios: AxiosInstance) => {
  return {
    assessment: {
      forPerson: (personId: string) =>
        apiAxios.request<ResponseBody<PersonAssessments, ErrorBody>>(
          config.assessment.forPerson(personId),
        ),
      /** One assessment, read once rather than watched; undefined for a 4xx, rejected for a 5xx or a failed request. */
      byId: async (
        assessmentId: AssessmentId,
      ): Promise<Assessment | undefined> => {
        const { url, method } = config.assessment.byId(assessmentId);
        const response = await apiAxios.request<unknown>({ url, method });

        return response.status === 200
          ? AssessmentSchema.parse(response.data)
          : undefined;
      },
      /**
       * The assessment as a PDF to save, named as the server names it;
       * undefined for a 4xx, rejected for a 5xx or a failed request.
       */
      pdf: async (
        assessmentId: AssessmentId,
      ): Promise<{ blob: Blob; filename: string } | undefined> => {
        const response = await apiAxios.request<unknown>(
          config.assessment.pdf(assessmentId),
        );

        const disposition: unknown = response.headers["content-disposition"];

        return response.status === 200 && response.data instanceof Blob
          ? {
              blob: response.data,
              filename:
                attachmentFilenameOf(
                  typeof disposition === "string" ? disposition : undefined,
                ) ?? "assessment.pdf",
            }
          : undefined;
      },
      start: async (personId: PersonId, body: StartAssessmentRequest) => {
        const response = await apiAxios.request<unknown>(
          config.assessment.start(personId, body),
        );

        return {
          status: response.status,
          data: parseStarted(response),
        };
      },
      /** What starting an assessment now would be offered to carry over. */
      carryOverOffer: async (personId: PersonId) => {
        const response = await apiAxios.request<unknown>(
          config.assessment.carryOverOffer(personId),
        );

        return {
          status: response.status,
          data:
            response.status === 200
              ? CarryOverOfferSchema.parse(response.data)
              : undefined,
        };
      },
      submit: async (
        assessmentId: AssessmentId,
        body: SubmitAssessmentRequest,
      ) => {
        const response = await apiAxios.request<unknown>(
          config.assessment.submit(assessmentId, body),
        );

        return {
          status: response.status,
          data: parseSubmitted(response),
        };
      },
      delete: (assessmentId: AssessmentId, body: DeleteAssessmentRequest) =>
        apiAxios.request<ResponseBody<NoContentBody, ErrorBody>>(
          config.assessment.delete(assessmentId, body),
        ),
      decide: async (
        assessmentId: AssessmentId,
        submissionNumber: SubmissionNumber,
        body: DecideAssessmentRequest,
      ) => {
        const response = await apiAxios.request<unknown>(
          config.assessment.decide(assessmentId, submissionNumber, body),
        );

        return {
          status: response.status,
          data: parseDecided(response),
        };
      },
    },
  };
};

export const apiHooks = () => {
  return {
    assessment: {
      /**
       * Every assessment under way about a young person, live on their whole merge tree.
       *
       * The tree is read off `person`, the record as the caller holds it, rather
       * than off this response: an assessment names only the record it was
       * started under, so a list with nothing in it yet names no record at all.
       * Until the record arrives, the id asked for is watched alone.
       */
      forPerson: function usePersonAssessments(
        personId: string,
        person: Person | undefined,
      ) {
        return useLiveApiAxios<PersonAssessments>(
          [
            notificationChannels.person(personId),
            ...(person === undefined ? [] : personChannels(person)),
          ],
          config.assessment.forPerson(personId),
        );
      },
      /**
       * The most recently finished of a young person's assessments, live on
       * their whole merge tree for the reason `forPerson` gives. `manual` holds
       * the read back until it is wanted.
       */
      historyForPerson: function usePersonAssessmentHistory(
        personId: string,
        person: Person | undefined,
        paging: { limit: number },
        options?: { manual?: boolean },
      ) {
        return useLiveApiAxios<AssessmentHistoryPage>(
          [
            notificationChannels.person(personId),
            ...(person === undefined ? [] : personChannels(person)),
          ],
          config.assessment.historyForPerson(personId, paging),
          options,
        );
      },
      /** One assessment, live on the record it was started under. */
      byId: function useAssessment(assessmentId: string) {
        return useLiveApiAxios<Assessment>(
          (assessment) =>
            assessment === undefined
              ? undefined
              : [notificationChannels.person(assessment.personId)],
          config.assessment.byId(assessmentId),
        );
      },
    },
  };
};
